import {
    ChangeEvent,
    forwardRef,
    ForwardRefExoticComponent,
    KeyboardEvent,
    ReactElement,
    Ref,
    RefAttributes,
    TextareaHTMLAttributes,
    useEffect,
    useRef,
} from 'react'
import classNames from 'classnames'

import './Textarea.scss'

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement>, RefAttributes<HTMLTextAreaElement> {
    title?: string
    error?: string
    autoGrow?: boolean
    minRows?: number
    maxRows?: number
    onSubmit?: () => void
}

const resizeToFitContent = (element: HTMLTextAreaElement, maxRows?: number): void => {
    element.style.height = 'auto'

    const lineHeight = parseFloat(getComputedStyle(element).lineHeight)
    const maxHeight = maxRows && ! Number.isNaN(lineHeight) ? lineHeight * maxRows : Infinity
    const nextHeight = Math.min(element.scrollHeight, maxHeight)

    element.style.height = `${nextHeight}px`
    element.style.overflowY = element.scrollHeight > maxHeight ? 'auto' : 'hidden'
}

export const Textarea: ForwardRefExoticComponent<TextareaProps> = forwardRef(({
    error = '',
    autoGrow = false,
    minRows = 1,
    maxRows,
    rows,
    value,
    onSubmit,
    onChange,
    onKeyDown,
    className = '',
    ...textareaProps
}, forwardedRef: Ref<HTMLTextAreaElement>): ReactElement => {
    const internalRef = useRef<HTMLTextAreaElement | null>(null)

    const setRefs = (element: HTMLTextAreaElement | null): void => {
        internalRef.current = element

        if (typeof forwardedRef === 'function') {
            forwardedRef(element)
        } else if (forwardedRef) {
            forwardedRef.current = element
        }
    }

    useEffect(() => {
        if (autoGrow && internalRef.current) {
            resizeToFitContent(internalRef.current, maxRows)
        }
    }, [autoGrow, maxRows, value])

    const handleChange = (event: ChangeEvent<HTMLTextAreaElement>): void => {
        onChange?.(event)

        if (autoGrow) {
            resizeToFitContent(event.currentTarget, maxRows)
        }
    }

    const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>): void => {
        onKeyDown?.(event)

        if (event.defaultPrevented) {
            return
        }

        if (event.key === 'Enter' && ! event.shiftKey && ! event.nativeEvent.isComposing) {
            event.preventDefault()
            onSubmit?.()
        }
    }

    const textareaClassNames = classNames('textarea', className, {
        'textarea--has-error': !! error,
        'textarea--auto-grow': autoGrow,
    })

    return (
        <textarea
            {...textareaProps}
            ref={setRefs}
            rows={rows ?? minRows}
            value={value}
            className={textareaClassNames}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
        />
    )
})
